import { useState, useRef, useEffect, useCallback } from 'react';
import { User, Sun, Download, Share } from 'lucide-react';
import InstagramOCR from './InstagramOCR';
import Avatar from './Avatar';
import AvatarService from './AvatarService';
import config from './config';

function CringeAsAServiceCard({ profileData, colorScheme, avatarKey, avatarUrl, onAvatarChange, onStartupContentChange, initialStartupContent }) {
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  const [isDownloadHovered, setIsDownloadHovered] = useState(false);
  const [isStoryHovered, setIsStoryHovered] = useState(false);
  const [startupContent, setStartupContent] = useState(
    initialStartupContent || {
      startupName: "LOADING...",
      startupDescription: "Pivoting to a new business model..."
    }
  );
  const [isLoading, setIsLoading] = useState(!initialStartupContent);
  const [imageLoading, setImageLoading] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [hasInitialContent] = useState(!!initialStartupContent);
  const [apiCallMade, setApiCallMade] = useState(false);
  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  
  // Create a ref to store timeout ID for debouncing
  const apiTimeoutRef = useRef(null);

  // OPTIMIZATION: Cleanup function
  useEffect(() => {
    return () => {
      // Clear any pending timeouts to prevent memory leaks
      if (apiTimeoutRef.current) {
        clearTimeout(apiTimeoutRef.current);
      }
    };
  }, []);

  // Only run once to set initial startup content or fetch new content
  useEffect(() => {
    // For cached cards (coming from DynamoDB), ALWAYS use the exact content
    // that was retrieved, and never make a new API call
    if (hasInitialContent) {
      console.log("IMPORTANT: Using cached startup content:", initialStartupContent);
      setStartupContent(initialStartupContent);
      setIsLoading(false);
      setApiCallMade(true); // Prevent any future API calls for this content
      
      // Notify parent component of the startup content
      if (onStartupContentChange) {
        onStartupContentChange(initialStartupContent);
      }
      return;
    }

    // Additional check for cached content passed via profileData
    if (profileData && profileData.lockContent === true && initialStartupContent) {
      console.log("LOCKED CONTENT DETECTED - using provided startup content");
      setStartupContent(initialStartupContent);
      setIsLoading(false);
      setApiCallMade(true);
      
      if (onStartupContentChange) {
        onStartupContentChange(initialStartupContent);
      }
      return;
    }

    // Extra safety check - completely disable API calls for cached data
    if (profileData && (profileData.cached === true || profileData.lockContent === true)) {
      console.log("CACHED PROFILE DETECTED - skipping API call completely");
      setApiCallMade(true);
      setIsLoading(false); // Ensure loading state is cleared
      return;
    }

    // Skip if we've already made an API call, if one is in progress, or if this is a cached card
    const isCachedCard = initialStartupContent && Object.keys(initialStartupContent).length > 0;
    if (apiCallMade || !profileData || apiCallInProgress || isCachedCard) {
      return;
    }

    // Clear any existing timeout to prevent duplicate calls
    if (apiTimeoutRef.current) {
      clearTimeout(apiTimeoutRef.current);
    }
    
    // Set a visual loading state immediately
    setIsLoading(true);
    
    // Define the fetch function
    const fetchStartupContent = async () => {
      // Set flags to prevent duplicate calls
      setApiCallMade(true);
      setApiCallInProgress(true);
      
      try {
        console.log("Fetching new startup content for profile:", profileData.id);
        const response = await fetch(`${config.apiUrl}/generate-startup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: profileData.id,
            bio: profileData.bio,
            profileBrief: profileData.profileBrief,
            contentType: profileData.contentType,
            followers: profileData.followers,
            following: profileData.following,
            posts: profileData.posts,
            highlights: profileData.highlights
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to generate startup content');
        }
        
        const data = await response.json();
        
        // Check for rate limiting or duplicate request responses
        if (data.startupName === "RATE LIMITED") {
          console.warn("Request was rate limited:", data.startupDescription);
          // Still update the UI to show why generation failed
          setStartupContent(data);
        } else {
          setStartupContent(data);
          
          // Notify parent component of the startup content
          if (onStartupContentChange) {
            onStartupContentChange(data);
          }
        }
      } catch (error) {
        console.error('Error fetching startup content:', error);
        setStartupContent({
          startupName: "GENERATION FAILED",
          startupDescription: "Could not create startup identity"
        });
      } finally {
        setIsLoading(false);
        
        // Allow another API call after a cooldown period (5 seconds)
        setTimeout(() => {
          setApiCallInProgress(false);
        }, 5000);
      }
    };
    
    // Debounce the API call with a 300ms delay to prevent double-clicks
    apiTimeoutRef.current = setTimeout(fetchStartupContent, 300);
  }, [profileData, hasInitialContent, initialStartupContent, onStartupContentChange, apiCallMade, apiCallInProgress]);

  useEffect(() => {
    if (avatarUrl) {
      console.log("🖼️ Starting image conversion for URL:", avatarUrl);
      
      const convertImageToDataUrl = (retryCount = 0) => {
        const img = new Image();
        
        // Critical for iOS Safari - set crossOrigin BEFORE src
        img.crossOrigin = "anonymous";
        
        img.onload = () => {
          try {
            console.log(`✅ Image loaded successfully, converting to data URL (attempt ${retryCount + 1})`);
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            
            const ctx = canvas.getContext('2d');
            
            // Clear canvas first (helps with iOS Safari)
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw image
            ctx.drawImage(img, 0, 0);
            
            // Convert with maximum quality
            const dataUrl = canvas.toDataURL('image/png', 1.0);
            setImageDataUrl(dataUrl);
            console.log("🎨 Image converted to data URL successfully");
            
            // OPTIMIZATION: Clean up canvas to free memory
            canvas.remove();
          } catch (error) {
            console.error('Error converting image to data URL:', error);
            
            // On iOS Safari, sometimes we need to retry
            if (retryCount < 2) {
              console.log(`🔄 Retrying image conversion (attempt ${retryCount + 2})`);
              setTimeout(() => convertImageToDataUrl(retryCount + 1), 1000);
            } else {
              console.error('❌ Failed to convert image after multiple attempts');
            }
          }
        };
        
        img.onerror = (error) => {
          console.error('Error loading image for conversion:', error);
          
          // Retry with a different approach on iOS Safari
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          if (isIOS && retryCount < 2) {
            console.log(`🔄 Retrying image load on iOS (attempt ${retryCount + 2})`);
            setTimeout(() => convertImageToDataUrl(retryCount + 1), 1000);
          }
        };
        
        // Add cache busting and set src last
        const urlWithCacheBust = profileData?.cached ? 
          `${avatarUrl}?t=${Date.now()}` : 
          `${avatarUrl}?cb=${Date.now()}`;
        
        console.log("🔄 Loading image for conversion:", urlWithCacheBust);
        img.src = urlWithCacheBust;
      };
      
      // Start the conversion process
      convertImageToDataUrl();
    }
  }, [avatarUrl, profileData?.cached]);

  const { startupName, startupDescription } = startupContent;

  const handleAvatarChange = (data) => {
    onAvatarChange(data);
  };

  const primaryBackground = colorScheme?.bg || "#8B5CF6";
  const lightBackground = colorScheme?.light || "#C4B5FD";
  const unicornBackground = colorScheme?.bg || "#8B5CF6";

  const containerStyle = {
    width: 'min(90vw, 540px)',
    aspectRatio: '9 / 16',
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "visible",
  };

  const backgroundStyle = {
    position: "absolute",
    top: "-20px",
    left: "-20px",
    right: "-20px",
    bottom: "-20px",
    borderRadius: "30px",
    background: `linear-gradient(45deg, transparent, ${lightBackground}33, transparent)`,
    animation: "rotateBg 12s linear infinite",
    zIndex: 0,
  };

  const cardStyle = {
    width: "80%",
    border: "8px solid black",
    borderRadius: "20px",
    overflow: "hidden",
    fontFamily: "Impact, sans-serif",
    textAlign: "center",
    background: primaryBackground,
    color: "black",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    animation: "bounceIn 0.8s ease-out",
    zIndex: 1,
  };

  const titleStyle = {
    padding: "clamp(12px, 3vw, 18px) 0",
    borderBottom: "3px solid black",
    fontSize: "clamp(24px, 6vw, 36px)",
    lineHeight: "1",
    fontWeight: "bold",
  };

  const avatarSectionStyle = {
    padding: "0",
    margin: "0",
    borderBottom: "3px solid black",
    background: primaryBackground,
    position: "relative",
    height: "clamp(150px, 20vw, 200px)",
    overflow: "hidden",
    fontSize: "0",
  };

  const serviceTextStyle = {
    fontSize: "clamp(26px, 5vw, 38px)",
    fontWeight: "bold",
    padding: "10px 0 0 0",
    lineHeight: "1",
    letterSpacing: "0px",
  };

  const founderTextStyle = {
    fontSize: "clamp(18px, 3vw, 24px)",
    padding: "clamp(5px, 1vw, 10px) clamp(15px, 3vw, 20px) clamp(10px, 2vw, 15px)",
    lineHeight: "1.2",
  };

  const statsContainerStyle = {
    display: "flex",
    borderBottom: "3px solid black",
  };

  const statColumnStyle = {
    flex: "1",
    padding: "clamp(8px, 2vw, 10px) 0",
    background: lightBackground,
    borderRight: "2px solid black",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    transition: "transform 0.3s ease",
  };

  const statNumberStyle = {
    fontSize: "clamp(22px, 4vw, 32px)",
    fontWeight: "bold",
    lineHeight: "1",
    marginBottom: "2px",
  };

  const statLabelStyle = {
    fontSize: "clamp(14px, 2.5vw, 20px)",
    lineHeight: "1.2",
    marginBottom: "clamp(5px, 1vw, 10px)",
  };

  const footerStyle = {
    padding: "clamp(10px, 2vw, 15px) 0",
    fontSize: "clamp(28px, 5vw, 40px)",
    fontWeight: "bold",
    background: unicornBackground,
  };

  const buttonStyle = {
    position: "absolute",
    bottom: "20px",
    border: `3px solid ${primaryBackground}`,
    borderRadius: "50%",
    width: "clamp(40px, 10vw, 60px)",
    height: "clamp(40px, 10vw, 60px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    boxShadow: `0 2px 5px ${primaryBackground}66`,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    background: "#ffffff",
  };

  const downloadButtonStyle = {
    ...buttonStyle,
    right: "20px",
  };

  const storyButtonStyle = {
    ...buttonStyle,
    right: "clamp(90px, 18vw, 100px)",
  };

  const buttonHoverStyle = {
    transform: "scale(1.1)",
    boxShadow: `0 3px 8px ${primaryBackground}88`,
  };

  const downloadImage = () => {
    if (!cardRef.current) return;
    
    setImageLoading(true);
    
    // Detect iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;
    
    const cardElement = cardRef.current;
    const cardWidth = cardElement.offsetWidth + 40;
    const cardHeight = (cardWidth * 16) / 9;
    
    setTimeout(() => {
      import('html-to-image').then((htmlToImage) => {
        // Enhanced options for iOS Safari
        const options = {
          quality: 1,
          backgroundColor: null,
          canvasWidth: cardWidth,
          canvasHeight: cardHeight,
          skipFonts: false,
          pixelRatio: 2,
          // Critical for iOS Safari - ensure all images are converted to data URLs
          useCORS: true,
          allowTaint: false,
          // Add a filter to ensure all images use data URLs
          filter: (node) => {
            // Convert any img elements to use data URLs if possible
            if (node.tagName === 'IMG' && node.src && !node.src.startsWith('data:')) {
              console.log('Found external image in card:', node.src);
              
              // If we have the data URL version, use it
              if (imageDataUrl && node.src.includes('amazonaws.com')) {
                console.log('Replacing S3 URL with data URL for canvas conversion');
                node.src = imageDataUrl;
              }
            }
            return true;
          }
        };
        
        htmlToImage.toCanvas(cardRef.current, options)
          .then((canvas) => {
            try {
              const dataUrl = canvas.toDataURL('image/png', 1.0);
              
              if (isIOSSafari) {
                // iOS Safari specific handling
                console.log('iOS Safari detected - using special download method');
                
                // Try Web Share API first (works well on iOS)
                if (navigator.share) {
                  fetch(dataUrl)
                    .then(res => res.blob())
                    .then(blob => {
                      const file = new File([blob], 'startup-identity.png', { type: 'image/png' });
                      return navigator.share({
                        files: [file],
                        title: 'My Startup Identity Card',
                        text: 'Check out my startup identity!'
                      });
                    })
                    .catch(shareError => {
                      console.log('Web Share failed, trying alternative method:', shareError);
                      // Fallback: Open in new tab for manual save
                      const newWindow = window.open();
                      newWindow.document.write(`
                        <html>
                          <head><title>Download Startup Identity Card</title></head>
                          <body style="margin:0; padding:20px; text-align:center; font-family: Arial, sans-serif;">
                            <h2>Your Startup Identity Card</h2>
                            <p>Tap and hold the image below, then select "Save to Photos"</p>
                            <img src="${dataUrl}" style="max-width:100%; height:auto; border-radius:10px; box-shadow: 0 4px 8px rgba(0,0,0,0.2);" />
                          </body>
                        </html>
                      `);
                      newWindow.document.close();
                    });
                } else {
                  // No Web Share API - open in new tab
                  const newWindow = window.open();
                  newWindow.document.write(`
                    <html>
                      <head><title>Download Startup Identity Card</title></head>
                      <body style="margin:0; padding:20px; text-align:center; font-family: Arial, sans-serif;">
                        <h2>Your Startup Identity Card</h2>
                        <p>Tap and hold the image below, then select "Save to Photos"</p>
                        <img src="${dataUrl}" style="max-width:100%; height:auto; border-radius:10px; box-shadow: 0 4px 8px rgba(0,0,0,0.2);" />
                      </body>
                    </html>
                  `);
                  newWindow.document.close();
                }
              } else {
                // Standard desktop download
                const link = document.createElement('a');
                link.download = 'startup-identity.png';
                link.href = dataUrl;
                link.click();
              }
            } catch (error) {
              console.error('Error generating download link:', error);
              
              // Ultimate fallback for any platform
              alert('Download failed. Please take a screenshot of the card instead.');
            } finally {
              setImageLoading(false);
            }
          })
          .catch((error) => {
            console.error('Error generating image:', error);
            
            // If canvas conversion fails, provide user instructions
            if (isIOSSafari) {
              alert('Unable to generate download. Please take a screenshot of the card using your device\'s screenshot feature.');
            } else {
              alert('Download failed. Please try again or take a screenshot.');
            }
            setImageLoading(false);
          });
      }).catch(err => {
        console.error('Failed to load html-to-image library:', err);
        alert('Download feature unavailable. Please take a screenshot of the card.');
        setImageLoading(false);
      });
    }, 500);
  };

  const shareToInstagramStory = async () => {
    if (!cardRef.current) return;

    setStoryLoading(true);
    let dataUrl = null;

    // Detect iOS Safari (same logic as download function)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOSSafari = isIOS && isSafari;

    const cardElement = cardRef.current;
    const cardWidth = cardElement.offsetWidth + 40;
    const cardHeight = (cardWidth * 16) / 9;

    try {
      const htmlToImage = await import('html-to-image');
      
      // Enhanced options for iOS Safari (same as download function)
      const options = {
        quality: 1,
        backgroundColor: null,
        canvasWidth: cardWidth,
        canvasHeight: cardHeight,
        skipFonts: false,
        pixelRatio: 2,
        // Critical for iOS Safari
        useCORS: true,
        allowTaint: false,
        // Replace S3 URLs with data URLs
        filter: (node) => {
          if (node.tagName === 'IMG' && node.src && !node.src.startsWith('data:')) {
            if (imageDataUrl && node.src.includes('amazonaws.com')) {
              console.log('Replacing S3 URL with data URL for Instagram share');
              node.src = imageDataUrl;
            }
          }
          return true;
        }
      };
      
      const canvas = await htmlToImage.toCanvas(cardRef.current, options);
      dataUrl = canvas.toDataURL('image/png', 1.0);
      
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'startup-identity.png', { type: 'image/png' });

      // Try Web Share API first (works great on iOS)
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Startup Identity',
          text: 'Check out my startup identity generated by Startup Identity Generator!'
        });
      } else if (isIOSSafari) {
        // iOS Safari fallback - open in new tab with share instructions
        const newWindow = window.open();
        newWindow.document.write(`
          <html>
            <head><title>Share to Instagram Story</title></head>
            <body style="margin:0; padding:20px; text-align:center; font-family: Arial, sans-serif;">
              <h2>Share to Instagram Story</h2>
              <p>Tap and hold the image below, then:</p>
              <ol style="text-align:left; max-width:300px; margin:10px auto;">
                <li>Select "Save to Photos"</li>
                <li>Open Instagram app</li>
                <li>Create a new story</li>
                <li>Select the saved image from your photos</li>
              </ol>
              <img src="${dataUrl}" style="max-width:100%; height:auto; border-radius:10px; box-shadow: 0 4px 8px rgba(0,0,0,0.2);" />
              <br><br>
              <button onclick="window.location.href='instagram://story'" style="background:#E4405F; color:white; border:none; padding:15px 30px; border-radius:25px; font-size:16px; cursor:pointer;">
                Open Instagram
              </button>
            </body>
          </html>
        `);
        newWindow.document.close();
      } else {
        // Standard fallback for other browsers
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'startup-identity.png';
        link.click();
        
        // Try to open Instagram app
        setTimeout(() => {
          window.location.href = 'instagram://story';
        }, 1000);
      }
    } catch (error) {
      console.error('Error sharing to Instagram story:', error);
      
      if (isIOSSafari) {
        alert('Unable to prepare share. Please take a screenshot of the card and share it manually to your Instagram story.');
      } else {
        // Fallback download
        if (dataUrl) {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'startup-identity.png';
          link.click();
        } else {
          alert('Share failed. Please try the download button instead.');
        }
      }
    } finally {
      setStoryLoading(false);
    }
  };

  const getCleanFollowerText = () => {
    if (profileData?.rawFollowersText) {
      const match = profileData.rawFollowersText.match(/(\d+\.?\d*\s*[KkMm]?)/i);
      return match ? match[1].trim().toUpperCase() : "3.2K";
    }
    return "3.2K";
  };

  const getCleanFollowingText = () => {
    if (profileData?.rawFollowingText) {
      const match = profileData.rawFollowingText.match(/(\d+\.?\d*\s*[KkMm]?)/i);
      return match ? match[1].trim().toUpperCase() : "256";
    }
    return "256";
  };

  const followers = getCleanFollowerText();
  const following = getCleanFollowingText();

  const calcAuraPoints = () => {
    if (!profileData) return "80";
    const posts = profileData.posts || 0;
    const followerCount = profileData.followers || 0;
    const followingCount = profileData.following || 0;
    const calculatedPoints = Math.min(999, posts*17 + followerCount - followingCount);
    return calculatedPoints > 0 ? calculatedPoints.toLocaleString() : "0";
  };

  const auraPoints = calcAuraPoints();
  const footerText = profileData?.id ? `@${profileData.id.replace('@','')}`: "@UNICORN";

  return (
    <div style={containerStyle}>
      <div ref={cardRef} style={{ 
        position: "relative", 
        width: "100%", 
        aspectRatio: "9 / 16", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center" 
      }}>
        <div style={backgroundStyle}></div>
        <div style={cardStyle}>
          <div style={titleStyle}>If you were a start-up, you would be named</div>
          <div style={avatarSectionStyle}>
            {avatarUrl ? (
              <img 
                ref={imageRef}
                src={profileData?.cached ? `${imageDataUrl || avatarUrl}#${Date.now()}` : (imageDataUrl || avatarUrl)} 
                alt="Avatar" 
                crossOrigin="anonymous"
                onLoad={() => {
                  console.log(`🎉 Image successfully loaded in component! URL: ${imageDataUrl || avatarUrl}`);
                }}
                onError={(error) => {
                  console.error(`💥 Image failed to load in component! URL: ${imageDataUrl || avatarUrl}`, error);
                  console.error(`💥 Error details:`, error.target?.src, error.target?.complete, error.target?.naturalWidth);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  margin: "0",
                  padding: "0"
                }}
              />
            ) : (
              <div style={{
                width: "100%", 
                height: "100%", 
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                {/* Show loading for cached cards while avatar loads, or Avatar component for new cards */}
                {profileData && profileData.cached ? (
                  <div style={{
                    fontSize: "18px",
                    color: "#666"
                  }}>
                    Loading avatar...
                  </div>
                ) : (
                  <Avatar 
                    size={140}
                    initialAvatarUrl={avatarUrl}
                    initialAvatarKey={avatarKey}
                    onAvatarChange={handleAvatarChange}
                  />
                )}
              </div>
            )}
          </div>
          <div style={{ borderBottom: "3px solid black", padding: "10px 0", background: primaryBackground }}>
            {isLoading ? (
              <div style={{ padding: "20px 0" }}>
                <div style={{ 
                  width: "30px", 
                  height: "30px", 
                  margin: "0 auto", 
                  border: "3px solid rgba(0,0,0,0.1)", 
                  borderRadius: "50%", 
                  borderTopColor: "#000",
                  animation: "spin 1s linear infinite" 
                }}></div>
                <div style={{ fontSize: "clamp(14px, 2vw, 18px)", marginTop: "10px" }}>Generating startup identity...</div>
              </div>
            ) : (
              <>
                <div style={serviceTextStyle}>{startupName}</div>
                <div style={founderTextStyle}>{startupDescription}</div>
              </>
            )}
          </div>
          <div style={statsContainerStyle}>
            <div 
              style={statColumnStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              <div style={statNumberStyle}>{followers}</div>
              <div style={statLabelStyle}>followers</div>
              <User size={24} />
            </div>
            <div 
              style={statColumnStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              <div style={statNumberStyle}>{following}</div>
              <div style={statLabelStyle}>following</div>
              <User size={24} />
            </div>
            <div 
              style={{...statColumnStyle, borderRight: "none"}}
              onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              <div style={statNumberStyle}>{auraPoints}</div>
              <div style={statLabelStyle}>aura points</div>
              <Sun size={24} />
            </div>
          </div>
          <div style={footerStyle}>{footerText}</div>
        </div>
      </div>
      <div 
        style={{
          ...downloadButtonStyle, 
          ...(isDownloadHovered ? buttonHoverStyle : {})
        }} 
        onClick={downloadImage}
        onMouseEnter={() => setIsDownloadHovered(true)}
        onMouseLeave={() => setIsDownloadHovered(false)}
      >
        {imageLoading ? (
          <div style={{ 
            width: "clamp(14px, 2vw, 20px)", 
            height: "clamp(14px, 2vw, 20px)", 
            margin: "0 auto", 
            border: "2px solid rgba(0,0,0,0.1)", 
            borderRadius: "50%", 
            borderTopColor: primaryBackground,
            animation: "spin 1s linear infinite" 
          }}></div>
        ) : (
          <Download size={32} color={primaryBackground} />
        )}
      </div>
      <div 
        style={{
          ...storyButtonStyle, 
          ...(isStoryHovered ? buttonHoverStyle : {})
        }} 
        onClick={shareToInstagramStory}
        onMouseEnter={() => setIsStoryHovered(true)}
        onMouseLeave={() => setIsStoryHovered(false)}
      >
        {storyLoading ? (
          <div style={{ 
            width: "clamp(14px, 2vw, 20px)", 
            height: "clamp(14px, 2vw, 20px)", 
            margin: "0 auto", 
            border: "2px solid rgba(0,0,0,0.1)", 
            borderRadius: "50%", 
            borderTopColor: primaryBackground,
            animation: "spin 1s linear infinite" 
          }}></div>
        ) : (
          <Share size={32} color={primaryBackground} />
        )}
      </div>
    </div>
  );
}

export default function MicroCelebrityGenerator() {
  const [profileData, setProfileData] = useState(null);
  const [avatarKey, setAvatarKey] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [usedCachedCard, setUsedCachedCard] = useState(false);
  const [startupContent, setStartupContent] = useState(null);
  const [previewCard, setPreviewCard] = useState(false);
  const [selectedColor, setSelectedColor] = useState('purple');
  const [showWelcome, setShowWelcome] = useState(true);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  
  // Define fetchAvatar function to retrieve avatar from the server
  const fetchAvatar = async (key, isCached = false) => {
    if (!key) return;
    
    try {
      // For cached avatars, add cache=false param to prevent browser caching
      const cacheParam = isCached ? '?cache=false' : '';
      console.log(`🖼️ Fetching avatar ${key} (cached: ${isCached}) - usedCachedCard: ${usedCachedCard}`);
      
      const response = await fetch(`${config.apiUrl}/avatar/${key}${cacheParam}`);
      if (!response.ok) {
        throw new Error('Failed to fetch avatar');
      }
      
      const data = await response.json();
      console.log(`🖼️ Received avatar URL for ${key}:`, data.url);
      console.log(`🖼️ Full response data:`, data);
      setAvatarUrl(data.url);
      console.log(`🖼️ Avatar URL has been set! Component should now render.`);
      console.log(`🖼️ Current state - startupContent:`, !!startupContent, `avatarUrl:`, !!data.url);
      
      // Test if the URL is actually accessible
      const testImg = new Image();
      testImg.onload = () => {
        console.log(`✅ Avatar image ${key} loaded successfully!`);
      };
      testImg.onerror = (error) => {
        console.error(`❌ Failed to load avatar image ${key}:`, error);
      };
      testImg.crossOrigin = 'anonymous';
      testImg.src = data.url;
      
      // Double-check we're using the right key
      if (data.key !== key) {
        console.warn(`Avatar key mismatch! Requested ${key} but got ${data.key}`);
      }
    } catch (error) {
      console.error('Error fetching avatar:', error);
    }
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  
  const colorOptions = [
    { name: 'blue', bg: '#3B82F6', light: '#93C5FD', emoji: '💙' },
    { name: 'green', bg: '#10B981', light: '#6EE7B7', emoji: '💚' },
    { name: 'purple', bg: '#8B5CF6', light: '#C4B5FD', emoji: '💜' },
    { name: 'orange', bg: '#F97316', light: '#FDBA74', emoji: '🧡' }
  ];

  const handleColorClick = (color) => {
    setSelectedColor(color);
  };

  const handleDataExtracted = async (data) => {
    setIsGenerating(true);
    setError(null);
    setShowError(false);
    
    try {
      // Check if we're getting an existing card
      if (data.existingCard) {
        console.log("🚀 OPTIMIZED CACHED CARD LOADING");
        console.log("Found existing card data:", data);
        console.log("Avatar key:", data.avatarKey);
        console.log("Startup content:", data.startupContent);
        
        // OPTIMIZATION: Set all non-async state immediately
        setUsedCachedCard(true);
        setHasSavedCard(true);
        setStartupContent(data.startupContent);
        
        const profileDataWithCached = {
          ...data.profileData,
          cached: true,
          lockContent: true
        };
        setProfileData(profileDataWithCached);
        setPreviewCard(true); // Show UI immediately
        setIsGenerating(false); // Remove loading state immediately
        
        // OPTIMIZATION: Load avatar in parallel (non-blocking)
        if (data.avatarKey) {
          setAvatarKey(data.avatarKey);
          
          // Don't await - let it load in background
          fetchAvatar(data.avatarKey, true).then(() => {
            console.log("✅ Cached avatar loaded in background");
          }).catch(err => {
            console.error("❌ Failed to load cached avatar:", err);
            // Fallback: try to get any random avatar
            AvatarService.getRandomAvatar().then(fallbackData => {
              console.log("🔄 Using fallback avatar");
              setAvatarKey(fallbackData.key);
              setAvatarUrl(fallbackData.url);
            }).catch(fallbackErr => {
              console.error("❌ Fallback avatar also failed:", fallbackErr);
            });
          });
        }
        
        console.log("🎯 Cached card UI rendered immediately, avatar loading in background");
        return;
      }
      
      // Otherwise this is a new card
      console.log("Creating new card for profile");
      
      // Reset cached flags only for new cards
      setUsedCachedCard(false);
      setHasSavedCard(false);
      
      // Reset startup content for new cards so it gets fetched properly
      setStartupContent(null);
      
      try {
        // For new cards, get a random avatar
        console.log("Getting random avatar for new card");
        const avatarData = await AvatarService.getRandomAvatar();
        console.log("Selected avatar:", avatarData);
        setAvatarKey(avatarData.key);
        setAvatarUrl(avatarData.url);
      } catch (error) {
        console.error('Failed to fetch random avatar:', error);
      }
      
      // Set the profile data
      const extractedData = { ...data, id: data.id || 'username' };
      setProfileData(extractedData);
      setPreviewCard(true);
      
    } catch (err) {
      console.error('Error during card generation:', err);
      setError('Failed to generate card. Please try again.');
      setShowError(true);
    } finally {
      // Only set loading to false for new cards
      if (!data.existingCard) {
        setIsGenerating(false);
      }
    }
  };
  
  const handleAvatarChange = (data) => {
    setAvatarKey(data.key);
    setAvatarUrl(data.url);
  };

  const handleReset = () => {
    setPreviewCard(false);
    setProfileData(null);
    setStartupContent(null);
    setAvatarKey(null);
    setAvatarUrl(null);
    setUsedCachedCard(false);
    setHasSavedCard(false);
  };

  // New function to save card data to DynamoDB
  const saveCardToDatabase = useCallback(async (instagramId, avatarKey) => {
    // Don't save if we already have a cached card
    if (usedCachedCard) {
      console.log("Using cached card - skipping database save");
      return;
    }
    
    // Don't save if we don't have startup content yet
    if (!startupContent) {
      console.log("No startup content available yet - skipping database save");
      return;
    }
    
    try {
      console.log("Saving card data to database:", instagramId, "with content:", startupContent);
      const response = await fetch(`${config.apiUrl}/save-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instagramId,
          profileData,
          startupContent,
          avatarKey
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save card data');
      }
      
      console.log('Card data saved successfully');
    } catch (error) {
      console.error('Error saving card data:', error);
    }
  }, [usedCachedCard, startupContent, profileData]);

  // Update the useEffect for saving card data
  useEffect(() => {
    const saveCard = async () => {
      if (profileData && startupContent && profileData.id && 
          profileData.id.startsWith('@') && !hasSavedCard && !usedCachedCard) {
        // Add a slight delay to ensure all data is properly set
        setTimeout(() => {
          saveCardToDatabase(profileData.id, avatarKey);
          setHasSavedCard(true);
        }, 1000);
      } else if (usedCachedCard) {
        // Mark as saved immediately if using cached data to prevent any save attempts
        setHasSavedCard(true);
      }
    };

    saveCard();
  }, [profileData, startupContent, avatarKey, hasSavedCard, usedCachedCard, saveCardToDatabase]);

  const selectedColorObject = colorOptions.find(color => color.name === selectedColor) || colorOptions[0];

  return (
    <div className="app-container">
      <style jsx>{`
        .app-container {
          min-height: 100vh;
          background: #ffffff;
          color: #2d3748;
          padding: clamp(10px, 2vw, 20px);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          overflow-x: hidden;
        }

        .bg-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: radial-gradient(circle at 25px 25px, rgba(0, 0, 0, 0.02) 2%, transparent 0%),
                          radial-gradient(circle at 75px 75px, rgba(0, 0, 0, 0.02) 2%, transparent 0%);
          background-size: 100px 100px;
          pointer-events: none;
          z-index: 0;
        }

        .header {
          text-align: center;
          margin-bottom: clamp(24px, 5vw, 48px);
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 600px;
          animation: fadeInDown 0.8s ease-out;
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounceIn {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
            opacity: 1;
          }
          100% {
            transform: scale(1);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes rotateBg {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 100%; }
        }

        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }

        .animate-slide-up {
          animation: slideInUp 0.5s ease-out forwards;
        }

        .header-title {
          font-size: clamp(28px, 6vw, 42px);
          font-weight: 800;
          color: #1a202c;
          margin-bottom: clamp(8px, 2vw, 16px);
          letter-spacing: -0.5px;
          position: relative;
          display: inline-block;
        }

        .header-title::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 25%;
          width: 50%;
          height: 4px;
          background: linear-gradient(90deg, transparent, ${selectedColorObject.bg}, transparent);
          border-radius: 2px;
        }

        .header-subtitle {
          font-size: clamp(14px, 2.5vw, 18px);
          color: #718096;
          font-weight: 400;
          max-width: 400px;
          margin: 0 auto;
        }

        .intro-section {
          width: 100%;
          max-width: 900px;
          margin: clamp(32px, 6vw, 48px) auto clamp(24px, 5vw, 40px);
          padding: 0 clamp(16px, 3vw, 24px);
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .intro-image {
          width: 100%;
          height: auto;
          max-width: 800px;
          border-radius: clamp(12px, 2vw, 20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          background: white;
          padding: clamp(8px, 1.5vw, 16px);
        }

        .intro-image:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }

        .welcome-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 100;
          opacity: ${showWelcome ? 1 : 0};
          pointer-events: ${showWelcome ? 'all' : 'none'};
          transition: opacity 0.5s ease-out;
        }

        .welcome-content {
          text-align: center;
          transform: ${showWelcome ? 'scale(1)' : 'scale(0.98)'};
          transition: transform 0.5s ease-out;
        }

        .welcome-logo {
          font-size: clamp(48px, 10vw, 64px);
          margin-bottom: clamp(16px, 3vw, 24px);
          display: inline-block;
          animation: pulse 1.5s infinite alternate;
        }

        @keyframes pulse {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }

        .main-content {
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: fadeIn 0.8s ease-out 0.3s both;
        }

        .card {
          background: #ffffff;
          border-radius: clamp(16px, 3vw, 24px);
          padding: clamp(20px, 4vw, 32px);
          width: 100%;
          margin-bottom: clamp(20px, 4vw, 32px);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .card:hover {
          transform: translateY(-5px);
        }

        .color-options {
          display: flex;
          justify-content: center;
          gap: clamp(10px, 2vw, 20px);
          margin: clamp(24px, 5vw, 36px) 0;
        }

        .color-option {
          width: clamp(36px, 8vw, 48px);
          height: clamp(36px, 8vw, 48px);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(16px, 3vw, 20px);
          cursor: pointer;
          border: 3px solid transparent;
          transition: transform 0.2s ease, border-color 0.2s ease;
          position: relative;
        }

        .color-option::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: inherit;
          z-index: -1;
          opacity: 0.6;
          transform: scale(0);
          transition: transform 0.3s ease;
        }

        .color-option:hover::after {
          transform: scale(1.3);
        }

        .color-option:hover {
          transform: scale(1.1);
        }

        .try-again-button {
          border: none;
          padding: clamp(12px, 2vw, 16px) clamp(24px, 5vw, 40px);
          border-radius: clamp(10px, 2vw, 14px);
          font-weight: 600;
          font-size: clamp(14px, 2.5vw, 17px);
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: clamp(8px, 1.5vw, 10px);
          margin-top: clamp(20px, 4vw, 32px);
          position: relative;
          overflow: hidden;
        }

        .try-again-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }

        .try-again-button:hover::before {
          left: 100%;
        }

        .try-again-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 7px 14px rgba(0,0,0,0.1), 0 3px 6px rgba(0,0,0,0.08);
        }

        .try-again-button:active {
          transform: translateY(0px);
        }

        .preview-container {
          position: relative;
          width: 100%;
          margin: clamp(20px, 4vw, 32px) auto 0;
          overflow: visible;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .footer {
          margin-top: clamp(32px, 6vw, 64px);
          padding-bottom: clamp(10px, 2vw, 20px);
          text-align: center;
          font-size: clamp(12px, 2vw, 14px);
          color: #a0aec0;
          width: 100%;
          max-width: 600px;
          position: relative;
        }

        .footer::before {
          content: '';
          position: absolute;
          top: -32px;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 2px;
          background: linear-gradient(90deg, transparent, ${selectedColorObject.light}, transparent);
        }

        .cached-card-notice {
          background-color: #fffbeb;
          border-left: 4px solid #f59e0b;
          color: #92400e;
          padding: clamp(12px, 3vw, 16px);
          margin-bottom: clamp(16px, 4vw, 24px);
          border-radius: 6px;
          font-size: clamp(13px, 2.5vw, 14px);
          width: 100%;
        }

        .cached-card-notice p:first-child {
          font-weight: 600;
          margin-bottom: 4px;
        }

        .error-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 100;
        }

        .error-modal {
          background: white;
          padding: clamp(24px, 5vw, 32px);
          border-radius: 12px;
          max-width: 400px;
          width: 90%;
        }

        .error-title {
          color: #e53e3e;
          font-size: clamp(18px, 4vw, 24px);
          font-weight: 700;
          margin-bottom: clamp(12px, 2vw, 16px);
        }

        .error-message {
          margin-bottom: clamp(20px, 4vw, 24px);
          color: #4a5568;
        }

        .error-button {
          background: #e53e3e;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 100;
        }

        .loading-content {
          text-align: center;
          color: white;
        }

        .loading-spinner {
          width: 64px;
          height: 64px;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        @media (max-width: 768px) {
          .header-title {
            font-size: clamp(24px, 5vw, 36px);
          }
          .header-subtitle {
            font-size: clamp(12px, 2vw, 16px);
          }
          .intro-section {
            margin: clamp(24px, 4vw, 32px) auto clamp(16px, 3vw, 24px);
            padding: 0 clamp(12px, 2vw, 16px);
          }
          .intro-image {
            border-radius: clamp(8px, 1.5vw, 12px);
            padding: clamp(6px, 1vw, 10px);
          }
          .card {
            padding: clamp(16px, 3vw, 28px);
          }
        }

        @media (max-width: 480px) {
          .app-container {
            padding: clamp(8px, 1.5vw, 16px);
          }
          .header-title {
            font-size: clamp(20px, 4vw, 32px);
          }
          .header-subtitle {
            font-size: clamp(10px, 1.8vw, 15px);
            padding: 0 20px;
          }
          .intro-section {
            margin: clamp(16px, 3vw, 24px) auto clamp(12px, 2vw, 16px);
            padding: 0 clamp(8px, 1.5vw, 12px);
          }
          .intro-image {
            border-radius: clamp(6px, 1vw, 8px);
            padding: clamp(4px, 0.8vw, 6px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          }
          .try-again-button {
            padding: clamp(10px, 2vw, 14px) clamp(20px, 4vw, 32px);
            font-size: clamp(12px, 2vw, 16px);
          }
          .color-option {
            width: clamp(30px, 6vw, 42px);
            height: clamp(30px, 6vw, 42px);
            font-size: clamp(14px, 2.5vw, 18px);
          }
        }
      `}</style>

      <div className="welcome-overlay">
        <div className="welcome-content">
          <div className="welcome-logo" style={{color: selectedColorObject.bg}}>🚀</div>
        </div>
      </div>

      <div className="bg-pattern"></div>

      <header className="header">
        <h1 className="header-title">Turn Your Gram Into Startup Gold</h1>
        <p className="header-subtitle">Because every influencer is just one pivot away from being a tech founder</p>
      </header>

      {/* Intro/How-to-use section */}
      <div className="intro-section">
        <img 
          src="/download-and-flaunt.png" 
          alt="How to use GramCard - Screenshot your Instagram profile, Upload, Download & Flaunt" 
          className="intro-image"
        />
      </div>

      <div className="main-content">
        {!previewCard ? (
          <div 
            className="card animate-fade-in"
            style={{
              border: `1.5px solid ${selectedColorObject.light}`,
              boxShadow: `0 4px 12px -2px ${selectedColorObject.light}66, 0 2px 8px -3px ${selectedColorObject.light}4D`
            }}
          >
            <InstagramOCR onDataExtracted={handleDataExtracted} />
            <div className="color-options">
              {colorOptions.map((color) => (
                <div
                  key={color.name}
                  className={`color-option`}
                  style={{ 
                    backgroundColor: color.bg,
                    border: selectedColor === color.name ? `3px solid ${selectedColorObject.bg}` : `3px solid transparent`,
                    opacity: selectedColor === color.name ? 1 : 0.7,
                    boxShadow: selectedColor === color.name ? `0 0 10px ${color.light}`: 'none',
                  }}
                  onClick={() => handleColorClick(color.name)}
                >
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {usedCachedCard && (
              <div className="cached-card-notice">
                <p>Notice: This is a previously generated card</p>
                <p>To support our service, each Instagram profile can generate only one card.</p>
              </div>
            )}
            
            <div className="preview-container">
              {/* Only render the card if we have all required data */}
              {(() => {
                const shouldRender = !usedCachedCard || (startupContent && avatarUrl);
                console.log(`🔍 Render decision - usedCachedCard: ${usedCachedCard}, startupContent: ${!!startupContent}, avatarUrl: ${!!avatarUrl}, shouldRender: ${shouldRender}`);
                return shouldRender;
              })() && (
                <CringeAsAServiceCard
                  key={`card-${profileData?.id || 'default'}-${usedCachedCard ? 'cached' : 'new'}`}
                  profileData={profileData}
                  colorScheme={selectedColorObject}
                  avatarKey={avatarKey}
                  avatarUrl={avatarUrl}
                  onAvatarChange={handleAvatarChange}
                  onStartupContentChange={setStartupContent}
                  initialStartupContent={startupContent}
                />
              )}
              
              {/* Show loading for cached cards until both startup content and avatar are ready */}
              {(() => {
                const shouldShowLoading = usedCachedCard && (!startupContent || !avatarUrl);
                console.log(`🔍 Loading decision - usedCachedCard: ${usedCachedCard}, startupContent: ${!!startupContent}, avatarUrl: ${!!avatarUrl}, shouldShowLoading: ${shouldShowLoading}`);
                return shouldShowLoading;
              })() && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  fontSize: '18px',
                  color: '#666'
                }}>
                  Loading cached card...
                </div>
              )}
            </div>

            <button
              className="try-again-button"
              onClick={handleReset}
              style={{
                backgroundColor: selectedColorObject.bg,
                color: '#ffffff',
                boxShadow: `0 4px 14px -4px ${selectedColorObject.bg}`
              }}
            >
              Create Another
            </button>
          </>
        )}
      </div>
      
      <div className="footer">
        <p>Made with ✨ for your social clout</p>
      </div>

      {isGenerating && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-text">Generating your card...</p>
            <p className="loading-subtext">This may take a few seconds</p>
          </div>
        </div>
      )}

      {showError && (
        <div className="error-overlay">
          <div className="error-modal">
            <h2 className="error-title">Error</h2>
            <p className="error-message">{error}</p>
            <button
              onClick={() => setShowError(false)}
              className="error-button"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}